import { useLottie } from "lottie-react";
import styles from "./Circles.module.scss";
import { useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QuizContext } from "context/QuizContext";

const CircleAnimation = ({ animationData }: { animationData: string }) => {
  const options = {
    animationData: animationData,
    loop: true,
    autoplay: true,
  };

  const { View } = useLottie(options);

  return View;
};

export default function Circles({
  selectedCircle,
}: {
  selectedCircle: number;
}) {
  const [animationDataStore, setAnimationDataStore] = useState<string | null>(
    null
  );
  const [animationData, setAnimationData] = useState<string | null>("");
  const [isDoneLoading, setIsDoneLoading] = useState(false);
  const [circles, setCircles] = useState<Array<[string, string] | undefined>>(
    []
  );

  const { currentQuestion, questionIndex } = useContext(QuizContext);

  // Function to extract image and animation data
  const extractImageAndAnimation = (question?: QuestionEntity) => {
    const data: Array<[string, string | undefined]> = [];
    for (let i = 1; i <= 5; i++) {
      const image = question?.[`image_${i}` as keyof QuestionEntity] as string;
      const animation = question?.[`animation_${i}` as keyof QuestionEntity] as
        | string
        | undefined;
      if (image) {
        data.push([image, animation]);
      }
    }
    return data;
  };

  useEffect(() => {
    // Update circles state whenever question changes
    setCircles(
      extractImageAndAnimation(currentQuestion).map(([image, animation]) => [
        image,
        animation || "",
      ])
    );
  }, [currentQuestion]);

  useEffect(() => {
    if (currentQuestion && selectedCircle) {
      const animationKey = `animation_${selectedCircle}`;

      setAnimationDataStore(
        currentQuestion[animationKey as keyof QuestionEntity] as string
      );
    }
  }, [selectedCircle, questionIndex, circles, currentQuestion]);

  useEffect(() => {
    // need to use /* @vite-ignore */ to avoid vite from trying to import the file
    // before the build process
    // need to use to dynamically import json animation files for lottie
    // add this loading state to prevent a flicker of the old animation
    // when loading the next json file is being loaded
    setAnimationData(null);
    if (animationDataStore) {
      fetch(animationDataStore).then((res) => {
        res.json().then((data) => {
          setAnimationData(data);
          setIsDoneLoading(true);
        });
      });
    }
  }, [animationDataStore]);

  return (
    <div className={styles.circles}>
      <div className={styles.circlesWrapper}>
        {circles.map((circle, i) => (
          <div
            key={i}
            className={`${styles.circle} ${selectedCircle === i + 1 ? styles.active : ""} 
            ${selectedCircle === i + 1 ? styles[currentQuestion?.color ? currentQuestion.color : "blue"] : ""}`}
          >
            {selectedCircle === i + 1 ? (
              <AnimatePresence>
                {isDoneLoading && animationData ? (
                  <motion.div
                    key="circleAnimation"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <CircleAnimation animationData={animationData!} />
                  </motion.div>
                ) : (
                  <motion.img
                    key="circleImage"
                    src={circle![0]}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                )}
              </AnimatePresence>
            ) : (
              <img src={circle![0]} />
            )}
          </div>
        ))}
      </div>
      {currentQuestion?.answer_type == "goed/niet-goed" ? (
        <div className={styles.row}>
          <p>Helemaal niet goed</p>
          <p>Heel goed</p>
        </div>
      ) : (
        <div className={styles.row}>
          <p>Helemaal mee oneens</p>
          <p>Helemaal mee eens</p>
        </div>
      )}
    </div>
  );
}
